import React from 'react'
import { Check, AlertCircle, Loader2, Clock } from 'lucide-react'
import { cn } from 'lib/utils'
import type { StagedImage, StagedImageStatus } from './imageUploadWizard.types'

const STATUS_ICON: Record<StagedImageStatus, typeof Check> = {
	pending: Clock,
	uploading: Loader2,
	uploaded: Check,
	error: AlertCircle,
}

const STATUS_CLASS: Record<StagedImageStatus, string> = {
	pending: 'text-muted-foreground',
	uploading: 'text-primary animate-spin',
	uploaded: 'text-success',
	error: 'text-destructive',
}

const STATUS_LABEL: Record<StagedImageStatus, string> = {
	pending: 'Waiting',
	uploading: 'Uploading',
	uploaded: 'Uploaded',
	error: 'Failed',
}

interface UploadProgressStepProps {
	images: StagedImage[]
	/** Set once the DB writes begin, so the copy stops saying "uploading". */
	isCommitting?: boolean
}

/**
 * Per-file status rows, in the same shape the document library uses. Text rows
 * rather than a progress bar: with a handful of files the per-file state is the
 * useful information, and a failed row needs somewhere to put its reason.
 */
const UploadProgressStep: React.FC<UploadProgressStepProps> = ({ images, isCommitting }) => {
	const uploaded = images.filter(i => i.status === 'uploaded').length
	const failed = images.filter(i => i.status === 'error').length

	return (
		<div className="space-y-3">
			<p className="text-sm text-muted-foreground">
				{isCommitting
					? 'Saving notes and gallery entries...'
					: uploaded + ' of ' + images.length + ' uploaded' + (failed ? ', ' + failed + ' failed' : '')}
			</p>

			<ul className="divide-y divide-border rounded-lg border border-border">
				{images.map(image => {
					const Icon = STATUS_ICON[image.status]
					return (
						<li key={image.id} className="flex items-center gap-3 px-3 py-2">
							<img
								src={image.preview}
								alt=""
								className="h-8 w-8 shrink-0 rounded object-cover"
							/>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm text-foreground">{image.file.name}</div>
								{image.status === 'error' && image.errorMessage && (
									<div className="truncate text-xs text-destructive">
										{image.errorMessage}
									</div>
								)}
							</div>
							<span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
								{STATUS_LABEL[image.status]}
								<Icon className={cn('h-4 w-4', STATUS_CLASS[image.status])} />
							</span>
						</li>
					)
				})}
			</ul>
		</div>
	)
}

export default UploadProgressStep
